// ── useAuth — stan autoryzacji ────────────────────────────────────────────────
// Odpowiada za: przywrócenie sesji, logowanie, rejestrację, kasowanie konta.
// NIE zarządza danymi zawodnika ani trenera — przekazuje je przez callbacki.
//
// Callbacki:
//   onParentLogin({ me, playerIds, addingChild })
//     → może zwrócić "app", żeby pominąć ekran wyboru zawodnika (auto-select)
//     → undefined/"players" daje domyślny ekran wyboru
//   onCoachLogin({ me, teamIds, newTeam? })
//     → newTeam (tylko przy rejestracji, patrz confirmCode): drużyna właśnie
//       założona przez finalizeRegistration, do powitalnego komunikatu
//       o 30-dniowym pilocie (docs/PAYMENTS-PLAN.md §6.2)
//   onLogout()
//   onInstallCheck()
//
// Strategia „localStorage-first":
//   Powracający użytkownik (cache ma dokument konta) → nawigacja natychmiast,
//   loadMyData() leci w tle i budzi ekrany zdarzeniem `fl-sync-complete`.
//   Nowe urządzenie (pusty cache) → czekamy na loadMyData, bo tylko Firestore
//   wie, kim jest ten uid.
//
// ── CO ZNIKŁO W V2 I DLACZEGO ────────────────────────────────────────────────
// Cały aparat odpowiadający na pytanie „które z kont pod tym adresem należy do
// tego uid": resolveAccountIdFromLS, resolveAccountIdAfterSync, flaga
// `authoritative`, resolveCoachFromLS, emailHasParentRemnants, sprzątanie
// duplikatów przez cleanPhantoms i trzypoziomowa hierarchia fallbacków trenera
// (kotwica coach_uid_ → coach_index → CF findCoachAccount).
//
// Pytanie przestało istnieć: ścieżka /users/{authUid} JEST tożsamością, więc
// pod jednym uid może leżeć dokładnie jedno konto. Rozpoznanie po emailu, które
// stało za błędem separacji kont, nie ma już gdzie się pojawić.

import { useState, useEffect } from "react";
import { auth } from "../firebase.js";
import { onAuthStateChanged } from "firebase/auth";
import {
  authLogin, authRegister, authLogout, authDeleteAccount,
  loadMyData, getMe, getMyPlayerIds, getMyTeamIds,
  callStartEmailVerification, callResendEmailVerification,
  callConfirmEmailVerification, callDeleteAccount, callGetServerTime,
  getCacheOwner, setCacheOwner, wipeLocalCache,
} from "../storage.js";
import { discardLocalPushToken } from "../services/push.js";
import { isPasswordStrongEnough } from "../utils/constants.js";
import { setClockOffset } from "../utils/serverClock.js";

// Korekta zegara klienta — docs/PAYMENTS-PLAN.md §K4 (przegląd runda 3), patrz
// komentarz w utils/serverClock.js. Fire-and-forget po każdym (re)zalogowaniu:
// offline/błąd zostawia offset na 0 (dotychczasowe zachowanie), nic nie blokuje.
async function syncServerClock() {
  try {
    const { now } = await callGetServerTime();
    setClockOffset(now);
  } catch (e) {
    console.warn("[FL] syncServerClock nieudane — zostaję przy zegarze lokalnym:", e.message);
  }
}

// Rejestracja bez akceptacji regulaminu leci w tę samą ścieżkę co braki w polach:
// setErr i stop PRZED authRegister, czyli konto Auth w ogóle nie powstaje.
const CONSENT_REQUIRED = "Zaakceptuj Regulamin i potwierdź zapoznanie się z Polityką Prywatności";

// ── Stempel właściciela urządzenia ───────────────────────────────────────────
// Wywoływane natychmiast po każdym udanym uwierzytelnieniu, PRZED jakimkolwiek
// odczytem z cache'u. Inny uid niż zapisany oznacza, że urządzenie należało do
// innego konta (np. konto usunięte i założone od nowa na ten sam adres) —
// wtedy cały lokalny cache jest nieaktualny i leci do kosza.
//
// Eksportowane dla useAuth.test.js: to czysta funkcja decyzyjna, na której stoi
// separacja kont, i chcemy ją testować bez montowania hooka.
export function ensureCacheOwner(firebaseUid) {
  if (!firebaseUid) return;
  const owner = getCacheOwner();
  if (owner === firebaseUid) return;
  if (owner) {
    console.log("[FL] 🔄 Zmiana właściciela urządzenia — czyszczę lokalny cache");
    discardLocalPushToken().catch(() => {});
    wipeLocalCache();
  }
  setCacheOwner(firebaseUid);
}

export function useAuth({ onParentLogin, onCoachLogin, onLogout, onInstallCheck }) {
  const [screen, setScreen] = useState("login");
  const [sessionRestoring, setSessionRestoring] = useState(true);
  const [err, setErr] = useState("");
  const [lf, setLf] = useState({ email: "", pass: "" });
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotOk, setForgotOk] = useState("");
  const [rf, setRf] = useState({ email: "", pass: "" });
  const [regRole, setRegRole] = useState("parent"); // "parent" | "coach"
  const [coachForm, setCoachForm] = useState({ name: "", academy: "", birthYear: "", email: "", pass: "" });
  // Akceptacja regulaminu — wspólna dla obu ról, więc siedzi tu, a nie w rf/coachForm.
  // Startuje na false i App resetuje ją przy każdym wejściu na ekran rejestracji:
  // zaznaczony checkbox pokazany komuś, kto go nie kliknął, nie jest akceptacją.
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // ── Weryfikacja email + kod ────────────────────────────────────────────────
  const [vf, setVf] = useState({ code: "" });
  const [pendingRole, setPendingRole] = useState(null);   // "rodzic" | "trener"
  const [pendingEmail, setPendingEmail] = useState("");
  const [resendCooldownUntil, setResendCooldownUntil] = useState(0);

  /**
   * Kieruje na właściwy ekran na podstawie roli z dokumentu konta.
   *
   * Rola pochodzi z /users/{uid}, nie z claimów. Claimy mówią, do których
   * drużyn mam dostęp — nie kim jestem, i wchodzą do tokena z opóźnieniem.
   * Trzymanie roli w jednym miejscu oszczędza klasy błędów typu „trener bez
   * drużyny wygląda jak rodzic".
   */
  function routeTo(profile) {
    if (!profile) return;
    if (profile.role === "coach") {
      onCoachLogin({ me: profile, teamIds: getMyTeamIds() });
      setScreen("coach");
      return;
    }
    const playerIds = getMyPlayerIds();
    const hint = onParentLogin({ me: profile, playerIds, addingChild: playerIds.length === 0 });
    setScreen(hint || "players");
  }

  // ── Przywrócenie sesji po odświeżeniu strony ───────────────────────────────
  useEffect(() => {
    let done = false;

    // Bezpiecznik na milczące auth. `setSessionRestoring(false)` woła wyłącznie
    // callback niżej, więc gdy onAuthStateChanged nie odpali się nigdy — offline,
    // zablokowane IndexedDB, część trybów prywatnych — użytkownik zostawał na
    // ekranie „⚽ FootLog" bez końca. Czekanie w main.jsx tego NIE łapało:
    // przepuszczało montowanie Reacta dalej, a hook wisiał tak samo.
    //
    // Celowo NIE ustawia `done`. Gdyby auth odezwało się po czasie, routing ma
    // pójść normalnie — gasimy sam splash, żeby dało się zalogować ręcznie,
    // a nie rezygnujemy z przywrócenia sesji.
    const bezpiecznik = setTimeout(() => setSessionRestoring(false), 8000);

    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      clearTimeout(bezpiecznik);
      if (done) return;
      done = true;
      unsub();
      if (!firebaseUser) { setSessionRestoring(false); return; }

      ensureCacheOwner(firebaseUser.uid);
      syncServerClock();

      // Szybka ścieżka: dokument konta jest w cache'u → nawiguj natychmiast.
      const cached = getMe();
      if (cached) {
        routeTo(cached);
        setSessionRestoring(false);
        onInstallCheck?.();
        // Odświeżenie w tle; ekrany budzi `fl-sync-complete`.
        loadMyData().catch(() => {});
        return;
      }

      // Nowe urządzenie — bez danych lokalnie musimy poczekać.
      console.log("[FL] Nowe urządzenie — pobieram dane przed nawigacją");
      routeTo(await loadMyData());
      setSessionRestoring(false);
      onInstallCheck?.();
    });
    return () => { done = true; clearTimeout(bezpiecznik); unsub(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Logowanie ──────────────────────────────────────────────────────────────
  async function login() {
    if (!lf.email || !lf.pass) { setErr("Podaj email i hasło"); return; }
    const emailNorm = lf.email.toLowerCase().trim();

    let firebaseUid;
    try {
      firebaseUid = (await authLogin(emailNorm, lf.pass))?.uid || null;
    } catch (e) {
      setErr(e.message); return;
    }

    // uid ZWRÓCONY przez authLogin, nie auth.currentUser — patrz komentarz
    // w registerCoach().
    ensureCacheOwner(firebaseUid);
    syncServerClock();

    try {
      const cached = getMe();
      if (cached) {
        routeTo(cached);
        setErr("");
        onInstallCheck?.();
        loadMyData().catch(() => {});
        return;
      }

      const profile = await loadMyData();
      if (!profile) {
        // Konto istnieje w Auth, ale nie ma dokumentu w /users. W v2 dokument
        // powstaje wyłącznie w confirmEmailVerification, więc to rejestracja
        // porzucona po podaniu hasła, a przed wpisaniem kodu. Klient nie ma
        // uprawnień, żeby założyć ten dokument sam — i słusznie, bo email nie
        // jest jeszcze potwierdzony.
        setErr("Rejestracja nie została dokończona. Załóż konto ponownie — kod weryfikacyjny przyjdzie mailem.");
        await authLogout();
        return;
      }

      routeTo(profile);
      setErr("");
      onInstallCheck?.();
    } catch (e) {
      console.error("[FL] login post-auth error:", e);
      setErr("Wystąpił błąd podczas logowania — " + (e.message || "spróbuj ponownie"));
    }
  }

  // ── Rejestracja trenera, krok 1: Auth + wysyłka kodu ───────────────────────
  async function registerCoach() {
    if (!coachForm.name || !coachForm.academy || !coachForm.birthYear || !coachForm.email || !coachForm.pass) {
      setErr("Wypełnij wszystkie pola"); return;
    }
    if (!isPasswordStrongEnough(coachForm.pass)) {
      setErr("Hasło zbyt słabe — min. 6 znaków, unikaj oczywistych haseł"); return;
    }
    if (!acceptedTerms) { setErr(CONSENT_REQUIRED); return; }
    const emailNorm = coachForm.email.toLowerCase().trim();

    let newUid;
    try {
      newUid = (await authRegister(emailNorm, coachForm.pass))?.uid || null;
    } catch (e) {
      setErr(e.message); return;
    }
    // Świeże konto = świeży uid. Jeśli urządzenie miało poprzedniego
    // właściciela, jego cache leci teraz do kosza. Bierzemy uid ZWRÓCONY przez
    // authRegister, nie auth.currentUser: gdyby ten drugi nie był jeszcze
    // ustawiony, ensureCacheOwner po cichu by odpuścił i urządzenie zostałoby
    // nieostemplowane, czyli bez ochrony przed pokazaniem cudzych danych.
    ensureCacheOwner(newUid);

    // Ekran „Potwierdź e-mail" pokazujemy OD RAZU, bez czekania na wysyłkę kodu.
    // Dokument weryfikacyjny CF zapisuje PRZED wysyłką maila, a potwierdzenie
    // kodu czyta ten dokument, nie maila — więc do pokazania ekranu wysyłka nie
    // jest potrzebna. Puszczamy ją w tle; cold start funkcji i round-trip do
    // Resend (łącznie ~1–2 s) schodzą z odczuwalnej ścieżki.
    setPendingRole("trener");
    setPendingEmail(emailNorm);
    setResendCooldownUntil(Date.now() + 60_000);
    setVf({ code: "" });
    setErr("");
    setScreen("verify-code");

    // Błąd wysyłki pokazujemy na ekranie verify — dokument z kodem już istnieje,
    // więc „Wyślij kod ponownie" zadziała; zdejmujemy cooldown, by ponowna próba
    // była natychmiastowa. Konta NIE kasujemy — nieukończone rejestracje sprząta
    // cleanupAbandonedRegistrations.
    callStartEmailVerification("trener", {
      name: coachForm.name, academy: coachForm.academy, birthYear: coachForm.birthYear,
    }).catch((e) => {
      setResendCooldownUntil(0);
      setErr("Nie udało się wysłać kodu: " + (e.message || "spróbuj ponownie") + ". Użyj „Wyślij kod ponownie”.");
    });
  }

  // ── Rejestracja rodzica, krok 1 ────────────────────────────────────────────
  async function register() {
    if (!rf.email || !rf.pass) { setErr("Podaj email i hasło"); return; }
    if (!isPasswordStrongEnough(rf.pass)) {
      setErr("Hasło zbyt słabe — min. 6 znaków, unikaj oczywistych haseł"); return;
    }
    if (!acceptedTerms) { setErr(CONSENT_REQUIRED); return; }
    const emailNorm = rf.email.toLowerCase().trim();

    let newUid;
    try {
      newUid = (await authRegister(emailNorm, rf.pass))?.uid || null;
    } catch (e) {
      setErr(e.message); return;
    }
    ensureCacheOwner(newUid);   // patrz komentarz w registerCoach()

    // Jak w registerCoach: przełączamy na ekran kodu od razu, wysyłkę puszczamy
    // w tle (patrz komentarz tam).
    setPendingRole("rodzic");
    setPendingEmail(emailNorm);
    setResendCooldownUntil(Date.now() + 60_000);
    setVf({ code: "" });
    setErr("");
    setScreen("verify-code");

    callStartEmailVerification("rodzic", {}).catch((e) => {
      setResendCooldownUntil(0);
      setErr("Nie udało się wysłać kodu: " + (e.message || "spróbuj ponownie") + ". Użyj „Wyślij kod ponownie”.");
    });
  }

  // ── Rejestracja, krok 2: potwierdzenie kodu ────────────────────────────────
  // Konto w Firestore powstaje DOPIERO tutaj, po stronie CF — żeby nie zakładać
  // kont na cudzy adres. CF nadaje też trenerowi claim coachTeams, a
  // callConfirmEmailVerification wymusza odświeżenie tokena, więc getMyTeamIds()
  // niżej widzi już nową drużynę.
  async function confirmCode() {
    const code = vf.code.trim();
    if (!/^\d{6}$/.test(code)) { setErr("Podaj 6-cyfrowy kod"); return; }
    try {
      const result = await callConfirmEmailVerification(code);
      const profile = getMe();

      if (result.role === "coach") {
        onCoachLogin({ me: profile, teamIds: getMyTeamIds(), newTeam: { ...result.team, teamId: result.teamId } });
        setScreen("coach");
      } else {
        onParentLogin({ me: profile, playerIds: [], addingChild: true });
        setScreen("players");
      }

      setErr("");
      setVf({ code: "" });
      setPendingRole(null);
    } catch (e) {
      setErr(e.message || "Błędny kod");
    }
  }

  async function resendCode() {
    if (Date.now() < resendCooldownUntil) return;
    try {
      await callResendEmailVerification();
      setResendCooldownUntil(Date.now() + 60_000);
      setErr("");
    } catch (e) {
      setErr(e.message || "Nie udało się wysłać kodu");
    }
  }

  // Porzucenie rejestracji na ekranie kodu. W Firestore nie ma jeszcze niczego
  // do sprzątnięcia — zostaje konto Auth i pending doc, którym zajmuje się
  // cleanupAbandonedRegistrations.
  async function cancelVerification() {
    try { await authDeleteAccount(); } catch {}
    setPendingRole(null);
    setVf({ code: "" });
    setErr("");
    setScreen("login");
  }

  // ── Kasowanie konta ────────────────────────────────────────────────────────
  /**
   * Jedna droga dla rodzica i trenera. W v1 były dwie, bo klient sam kasował
   * dokumenty i musiał układać operacje tak, żeby wszystkie zdążyły przed
   * user.delete() — ten unieważnia token, a po nim Firestore odrzuca wszystko.
   * Teraz kasuje CF przez Admin SDK, razem z kontem Auth, więc kolejność
   * przestała być problemem klienta.
   */
  async function deleteFullAccount() {
    if (!window.confirm("Usunąć CAŁE konto? Zostaną usunięte wszystkie dane i profil z systemu. Tej akcji nie można cofnąć.")) return;
    try {
      await callDeleteAccount();
      await authLogout();
      onLogout?.();
      setScreen("login");
    } catch (e) {
      setErr(e.message);
    }
  }

  return {
    screen, setScreen,
    sessionRestoring,
    err, setErr,
    lf, setLf,
    showForgot, setShowForgot,
    forgotEmail, setForgotEmail,
    forgotOk, setForgotOk,
    rf, setRf,
    regRole, setRegRole,
    coachForm, setCoachForm,
    acceptedTerms, setAcceptedTerms,
    login,
    register,
    registerCoach,
    deleteFullAccount,
    // Weryfikacja email + kod
    vf, setVf,
    pendingRole, pendingEmail,
    resendCooldownUntil,
    confirmCode, resendCode, cancelVerification,
  };
}
